import { call, put, select, takeLatest } from "redux-saga/effects";

import * as globalSelectors from "Store/global/selectors";
import * as locationActions from "Store/locations/actions";
import * as locationTypes from "Store/locations/constants";
import { makeSelectActiveLocation, makeSelectCurrentLocation } from "Store/locations/selectors";
import { makeSelectCurrentUser } from "Store/user/selectors";

import ERRORS from "constants/localization/errors";
import SUCCESSES from "constants/localization/successes";
import { setActionSpinner, setFetchSpinner, setPagination } from "Store/global/actions";
import { convertMetaToPagination, renderApiParameters } from "Store/global/saga";
import { locationInfoInit } from "utils/initialObjects";
import { rewriteTheSameProperties } from "utils/objectsHelper";
import { Toast } from "utils/toast";
import {
  createBrandingApi,
  createLocationApi,
  fetchCurrentLocationApi,
  fetchLocationApi,
  fetchLocationsApi,
  fetchLocationSummaryApi,
  updateBrandingApi,
  updateBrandingBackgroundLogoApi,
  updateBrandingBlackLogoApi,
  updateBrandingColorLogoApi,
  updateLocationBrandingApi,
  updateLocationInfoApi,
  updateLocationSubdomainApi
} from "./api";

function* updateLocationLogos(newLogo: Branding, currentLogo: Branding, formData: FormData) {
  if (newLogo.color_logo && currentLogo.color_logo !== typeof newLogo.color_logo) {
    yield call(updateBrandingColorLogoApi, formData);
  }
  if (newLogo.black_logo && currentLogo.black_logo !== typeof newLogo.black_logo) {
    yield call(updateBrandingBlackLogoApi, formData);
  }
  if (newLogo.bg_image && currentLogo.bg_image !== typeof newLogo.bg_image) {
    yield call(updateBrandingBackgroundLogoApi, formData);
  }
}

function* handleFetchCurrentLocationSaga() {
  try {
    const { data, error } = yield fetchCurrentLocationApi();

    if (error) {
      Toast.error(error);
    }

    // Check if the user's window location matches the current location. If not redirect to Bryan's alternate url
    if (
      data.urls &&
      data.urls.domain &&
      window &&
      data.urls.domain !== window.location.hostname &&
      window.location.hostname !== "localhost"
    ) {
      return window.location.replace("https://notification.mysatcourse.com/");
    }

    // Move onto setting current location in redux
    const savedCurrentLocation = yield select(makeSelectActiveLocation);
    if (!savedCurrentLocation) {
      yield put(locationActions.handleSetActiveLocation(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchCurrentLocation, error);
  }
}

function* handleFetchLocationSaga(action: locationTypes.IHandleFetchLocation) {
  yield put(setFetchSpinner(true));
  try {
    const { locationId } = action.payload;
    const { error, data } = yield fetchLocationApi(locationId);
    if (error) {
      yield put(setFetchSpinner(false));
      Toast.error(ERRORS.handleFetchLocation, error);
    } else {
      yield put(locationActions.handleSetCurrentLocation(data));
      yield put(setFetchSpinner(false));
    }
  } catch (error) {
    yield put(setFetchSpinner(false));
    Toast.error(ERRORS.handleFetchLocation, error);
  }
}

function* handleFetchAllLocationsSaga() {
  yield put(setFetchSpinner(true));
  try {
    const activeUser = yield select(makeSelectCurrentUser);
    const parameters = yield renderApiParameters(globalSelectors, "locations");
    const query = parameters + "&stats=1";
    const { data, meta, error } = yield fetchLocationsApi(activeUser.id, query);

    if (error) {
      Toast.error(ERRORS.fetchAllLocations, error);
    } else {
      yield put(setPagination(convertMetaToPagination(meta), "locations"));
      yield put(locationActions.setLocations(data));
    }
  } catch (error) {
    Toast.error(ERRORS.fetchAllLocations, error);
  } finally {
    yield put(setFetchSpinner(false));
  }
}

function* handleCreateLocationSaga(action: locationTypes.IHandleCreateLocation) {
  yield put(setActionSpinner(true));
  try {
    const { location } = action.payload;
    const form = new FormData();
    let isCreatedLocation = false;

    const currentLocation: TLocation = yield select(makeSelectActiveLocation);
    const locationInfo = rewriteTheSameProperties(locationInfoInit, location) as LocationRequest;
    const body = { ...locationInfo, organization_id: currentLocation.organization.id };

    const brandingImageKeys = ["color_logo", "black_logo", "bg_image"];
    for (const [key, value] of Object.entries(location.branding)) {
      if (brandingImageKeys.includes(key)) {
        // Only send an image when a file was actually uploaded. Appending null
        // would send the literal string "null" and break the backend
        // file/image validation for unuploaded images.
        if (value instanceof Blob) {
          form.append(key, value);
          isCreatedLocation = true;
        }
        continue;
      }
      form.append(key, value);
      if (!!value) {
        isCreatedLocation = true;
      }
    }
    const { error, data } = yield createLocationApi(body);
    if (error) {
      yield put(setActionSpinner(false));
      return Toast.error(ERRORS.handleCreateLocation, error);
    } else {
      if (isCreatedLocation) {
        yield call(handleCreateBrandingSaga, {
          payload: { branding: form, locationId: data.id },
          type: "HANDLE_CREATE_BRANDING"
        });
      }
      yield put(setActionSpinner(false));
      Toast.success(SUCCESSES.handleCreateLocation);
      yield call(handleFetchAllLocationsSaga);
    }
  } catch (error) {
    yield put(setActionSpinner(false));
    Toast.error(ERRORS.handleCreateLocation, error);
  }
}

function* handleCreateBrandingSaga(action: locationTypes.IHandleCreateBranding) {
  try {
    const { branding, locationId } = action.payload;

    const { error, data } = yield call(createBrandingApi, branding);
    if (error) {
      Toast.error(ERRORS.handleCreateBrandingLongVersion, error);
    } else {
      yield call(handleUpdateLocationBrandingSaga, {
        payload: { body: { branding_id: data.branding.id, location_id: locationId } },
        type: "HANDLE_UPDATE_LOCATION_BRANDING"
      });
    }
  } catch (error) {
    Toast.error(ERRORS.handleCreateBrandingLongVersion, error);
  }
}

function* handleUpdateLocationBrandingSaga(action: locationTypes.IHandleUpdateLocationBranding) {
  try {
    const { body } = action.payload;
    const { error } = yield call(updateLocationBrandingApi, body);
    if (error) {
      Toast.error(ERRORS.handleUpdateLocationBranding, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateLocationBranding, error);
  }
}

function* handleUpdateLocationInfoSaga(action: locationTypes.IHandleUpdateLocationInfo) {
  try {
    const { locationInfo } = action.payload;
    const { error } = yield updateLocationInfoApi(locationInfo);
    if (error) {
      Toast.error(ERRORS.handleUpdateLocation, error);
    }
  } catch (error) {
    Toast.error(ERRORS.handleUpdateLocation, error);
  }
}

function* handleUpdateLocationSubdomainSaga(action: locationTypes.IHandleUpdateLocationSubdomain) {
  try {
    const { body } = action.payload;
    const { error } = yield updateLocationSubdomainApi(body);
    if (error) {
      return Toast.error(error);
    }
  } catch (error) {
    return Toast.error(error);
  }
}

function* handleUpdateBrandingSaga(action: locationTypes.IHandleUpdateBranding) {
  try {
    const { newBranding } = action.payload;
    const { error } = yield updateBrandingApi(newBranding);
    if (error) {
      return Toast.error(error);
    }
  } catch (error) {
    return Toast.error(error);
  }
}

function* handleUpdateLocationSaga(action: locationTypes.IHandleUpdateLocation) {
  yield put(setActionSpinner(true));
  try {
    const form = new FormData();
    const { location } = action.payload;
    const currentLocation: TLocation = yield select(makeSelectCurrentLocation);
    const locationInfo = rewriteTheSameProperties(locationInfoInit, location) as LocationRequest;
    const data = { ...locationInfo, location_id: location.id };

    for (const [key, value] of Object.entries(location.branding)) {
      form.append(key, value);
    }
    yield call(handleUpdateLocationInfoSaga, {
      payload: { locationInfo: data },
      type: "HANDLE_UPDATE_LOCATION_INFO"
    });
    if (location.subdomain !== currentLocation.subdomain) {
      const body = {
        location_id: location.id,
        subdomain: location.subdomain
      };
      yield call(handleUpdateLocationSubdomainSaga, {
        payload: { body },
        type: "HANDLE_UPDATE_LOCATION_SUBDOMAIN"
      });
    }
    yield call(updateLocationLogos, location.branding, currentLocation.branding, form);
    yield call(handleUpdateBrandingSaga, {
      payload: { newBranding: form },
      type: "HANDLE_UPDATE_BRANDING"
    });
    Toast.success(SUCCESSES.handleUpdateLocation);
    yield call(handleFetchLocationSaga, {
      payload: { locationId: location.id },
      type: "HANDLE_FETCH_LOCATION"
    });
    yield put(setActionSpinner(false));
  } catch (error) {
    yield put(setActionSpinner(false));
    return Toast.error(ERRORS.handleUpdateLocation, error);
  }
}
function* handleFetchActiveLocationSummarySaga(
  action: locationTypes.IHandleFetchActiveLocationSummary
) {
  try {
    const { locationId } = action.payload;
    const { data, error } = yield fetchLocationSummaryApi(locationId);

    if (error) {
      Toast.error(ERRORS.handleFetchActiveLocationSummary, error);
    } else {
      yield put(locationActions.setActiveLocationSummary(data));
    }
  } catch (error) {
    Toast.error(ERRORS.handleFetchActiveLocationSummary, error);
  }
}
export default function* locationsSaga() {
  yield takeLatest(locationTypes.HANDLE_FETCH_LOCATIONS, handleFetchAllLocationsSaga);
  yield takeLatest(locationTypes.HANDLE_FETCH_LOCATION, handleFetchLocationSaga);
  yield takeLatest(locationTypes.HANDLE_UPDATE_LOCATION_INFO, handleUpdateLocationInfoSaga);
  yield takeLatest(locationTypes.HANDLE_UPDATE_LOCATION, handleUpdateLocationSaga);
  yield takeLatest(locationTypes.HANDLE_UPDATE_LOCATION_BRANDING, handleUpdateLocationBrandingSaga);
  yield takeLatest(locationTypes.HANDLE_CREATE_BRANDING, handleCreateBrandingSaga);
  yield takeLatest(locationTypes.HANDLE_UPDATE_BRANDING, handleUpdateBrandingSaga);
  yield takeLatest(locationTypes.HANDLE_CREATE_LOCATION, handleCreateLocationSaga);
  yield takeLatest(
    locationTypes.HANDLE_UPDATE_LOCATION_SUBDOMAIN,
    handleUpdateLocationSubdomainSaga
  );
  yield takeLatest(
    locationTypes.HANDLE_FETCH_ACTIVE_LOCATION_SUMMARY,
    handleFetchActiveLocationSummarySaga
  );
  yield takeLatest(locationTypes.HANDLE_FETCH_CURRENT_LOCATION, handleFetchCurrentLocationSaga);
}
